import { RefreshFlag } from "@/refresh_flag.tsx"
import { useLocation } from "wouter-preact"
import { useFetch } from "@/utils"
import { api_fetch_throws, TransactionJson } from "@/api"
import { CenterSpinner } from "@/app/loading.tsx"
import { AccountingTransactionContent } from "./AccountingTransactionContent.tsx"
import {
  AccountingTransactionModalId,
  AccountingTransactionModalOpen,
  closeAccountingTransactionModal,
} from "./accounting_transaction_modal_state.tsx"
import { should_show_detail_spinner } from "@/utils/detail_refresh.ts"
import { Dialog, DialogOverlay, DialogPortal } from "@/components/ui/dialog.tsx"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, ExternalLink, Calculator, Link } from "lucide-react"
import { Button } from "@/components/ui/button.tsx"
import { toast } from "@/components/ui/use-toast.ts"

export function AccountingTransactionModal() {
  const transactionId = AccountingTransactionModalId.use((id: string | null) => id)
  const isOpen = AccountingTransactionModalOpen.use((open: boolean) => open)

  if (!transactionId) {
    return null
  }

  function handleInteractOutside(e: Event) {
    // Prevent closing when user selects from a reason code/account dropdown (AutocompleteSelect uses Popover + Command in a portal)
    const target = e.target as HTMLElement
    if (
      target?.closest?.("[data-radix-popper-content-wrapper]") ||
      target?.closest?.("[role='listbox']") ||
      target?.closest?.("[cmdk-list]") ||
      target?.closest?.("[cmdk-input]") ||
      target?.closest?.("[cmdk-item]")
    ) {
      e.preventDefault()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && closeAccountingTransactionModal()}>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          class="fixed left-[50%] top-4 z-50 w-[calc(100%-2rem)] max-w-full translate-x-[-50%] h-[calc(100vh-2rem)] border bg-background shadow-lg sm:rounded-lg flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          onInteractOutside={handleInteractOutside}>
          <AccountingTransactionModalContent transactionId={transactionId} />
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

interface AccountingTransactionModalContentProps {
  transactionId: string
}

function AccountingTransactionModalContent({ transactionId }: AccountingTransactionModalContentProps) {
  const statusFlag = RefreshFlag.use((sf: boolean) => sf!)
  const [, setLocation] = useLocation()

  const {
    data: transaction,
    loading,
  } = useFetch(async () => {
    return api_fetch_throws<TransactionJson>(`/transaction/${transactionId}`)
  }, [transactionId, statusFlag])

  function handleViewTransaction() {
    closeAccountingTransactionModal()
    setLocation(`/accounting/transaction/${transactionId}`)
  }

  function handleViewDeduction() {
    if (!transaction) return
    closeAccountingTransactionModal()
    setLocation(`/deduction/${transaction.id}`)
  }

  function handleCopyUrl() {
    const url = `${window.location.origin}/accounting/transaction/${transactionId}`
    navigator.clipboard.writeText(url)
    toast({
      title: "Link copied",
      description: "Transaction URL copied to clipboard",
    })
  }

  return (
    <>
      {/* Header */}
      <div class="flex items-center justify-between px-4 py-3 border-b bg-background shrink-0">
        <DialogPrimitive.Close class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <X className="h-5 w-5" />
          <span class="sr-only">Close</span>
        </DialogPrimitive.Close>
        <div class="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopyUrl}>
            <Link className="h-5 w-5" />
            Copy Link
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewTransaction}>
            <ExternalLink className="h-5 w-5" />
            View Transaction
          </Button>
          {transaction && (
            <Button variant="outline" size="sm" onClick={handleViewDeduction}>
              <Calculator className="h-5 w-5" />
              View Deduction
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div class="flex-1 overflow-auto">
        {/* Keep content mounted during background refetches so unsaved splits survive */}
        {!transaction || should_show_detail_spinner(loading, transaction.id, transactionId) ? (
          <CenterSpinner />
        ) : (
          <AccountingTransactionContent transaction={transaction} />
        )}
      </div>
    </>
  )
}
